import { Command } from 'commander';
import inquirer from 'inquirer';
import { getV2Client } from '../../api/v2-client.js';
import type { IV2Client } from '../../api/v2-client.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { handleV2Error } from '../../handle-error.js';
import { addGridOption, gridSlug, type GridOpts } from '../../grid-opt.js';

interface RevokeOpts extends GridOpts { skipConfirm?: boolean; }

interface PendingInvitation {
  invitation_id: string;
  invited_email: string;
  role: string;
  status: string;
}

async function resolveInvitationId(client: IV2Client, value: string): Promise<string> {
  // Arg is an invitation_id — use as-is.
  if (!value.includes('@')) return value;

  // Arg is an email — scan pending invitations for a match. MAX_LIST_LIMIT is
  // 100 server-side, so orgs with >100 pending invitations need pagination or
  // the match may live on page 2+ and produce a false "not found".
  const target = value.toLowerCase();
  const matches: PendingInvitation[] = [];
  let cursor: string | undefined;
  do {
    const params: Record<string, string | number | undefined> = { status: 'pending', limit: 100 };
    if (cursor) params.cursor = cursor;
    const resp = await client.get<{ invitations: PendingInvitation[]; next_cursor: string | null }>(
      '/api/v2/invitations', params);
    matches.push(...resp.invitations.filter((i) => i.invited_email.toLowerCase() === target));
    cursor = resp.next_cursor ?? undefined;
  } while (cursor);

  if (matches.length === 0) {
    throw new V2ApiError(404, 'INVITATION_NOT_FOUND',
      `No pending invitation for "${value}" in the active grid.`);
  }
  if (matches.length > 1) {
    // The API enforces one pending invitation per (org, email), but guard
    // against historical data anyway.
    throw new V2ApiError(409, 'AMBIGUOUS_INVITATION',
      `Multiple pending invitations for "${value}"; pass invitation_id directly.`);
  }
  return matches[0].invitation_id;
}

export async function runAdminRevoke(value: string, opts: RevokeOpts, client?: IV2Client): Promise<void> {
  const c = client ?? await getV2Client(gridSlug(opts));
  const invitationId = await resolveInvitationId(c, value);

  if (!opts.skipConfirm) {
    const label = value === invitationId ? invitationId : `${value} (${invitationId})`;
    const { ok } = await inquirer.prompt([{ type: 'confirm', name: 'ok',
      message: `Revoke invitation ${label}?`, default: false }]);
    if (!ok) { process.stdout.write('Cancelled.\n'); return; }
  }
  const resp = await c.post<{ already_revoked?: boolean }>(
    `/api/v2/invitations/${encodeURIComponent(invitationId)}/revoke`);
  process.stdout.write(resp.already_revoked ? `Invitation was already revoked.\n` : `✓ Revoked invitation.\n`);
}

export const adminRevokeCommand = new Command('revoke')
  .description('Revoke a pending invitation (admin only)')
  .argument('<invitation_id_or_email>', 'Invitation id, or email of the invited user')
  .option('--skip-confirm', "Don't prompt");
addGridOption(adminRevokeCommand, 'Override active grid');
adminRevokeCommand
  .action(async (value: string, opts: RevokeOpts) => {
    try { await runAdminRevoke(value, opts); }
    catch (err) { await handleV2Error(err); }
  });
